import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  EmailEditor,
  EmailEditorProvider,
  useEditorContext,
  BlockAvatarWrapper,
  useBlock,
} from "easy-email-editor";
import {
  BlockManager,
  BasicType,
  JsonToMjml,
  TemplateEngineManager,
} from "easy-email-core";
import { SimpleLayout } from "easy-email-extensions";
import {
  ConfigProvider,
  Button as ArcoButton,
  Input as ArcoInput,
} from "@arco-design/web-react";
import enUS from "@arco-design/web-react/es/locale/en-US";

import mjml from "mjml-browser";

import "@arco-design/web-react/dist/css/arco.css";
import "easy-email-editor/lib/style.css";
import "easy-email-extensions/lib/style.css";

import * as PR from "../../prime-modules/index";
import AdminHeader from "../layout/admin-header";
import AdminFooter from "../layout/admin-footer";
import "./EmailTemplates.scss";
import { PERMISSIONS, hasPermission } from "../../utils/permissions";
import {
  getEmailTemplates,
  getEmailTemplateDetails,
  updateEmailTemplate,
  deleteEmailTemplate,
  translateEmailTemplate,
  testSendEmailTemplate,
  getEditorConfig,
  updateEditorConfig,
} from "../../services/api";
import { useFormik } from "formik";
import * as Yup from "yup";

// ─── Shared panel styles & Components ─────────────────────────────────────────

const chipStyle = {
  flex: 1,
  padding: "5px 10px",
  background: "#f0f4ff",
  borderRadius: 4,
  cursor: "grab",
  fontSize: 13,
  border: "1px solid #d0d9ff",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function SaveBlockButton({ onSave }) {
  const { focusBlock } = useBlock();
  const [name, setName] = useState("");
  const isPage =
    !focusBlock || !focusBlock.type || focusBlock.type === BasicType.PAGE;

  function handle() {
    const trimmed = name.trim();
    if (!trimmed || isPage) return;
    onSave(trimmed, focusBlock);
    setName("");
  }

  return (
    <div style={{ padding: "0 12px 8px", width: "100%" }}>
      {isPage ? (
        <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 6px" }}>
          Select a block on the canvas first.
        </p>
      ) : (
        <p style={{ fontSize: 12, color: "#475569", margin: "0 0 6px" }}>
          Selected:{" "}
          <strong>{(focusBlock.type || "").replace("advanced#", "")}</strong>
        </p>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <ArcoInput
          value={name}
          onChange={(value) => setName(value)}
          onKeyDown={(e) => e.key === "Enter" && handle()}
          placeholder="Block name…"
          disabled={isPage}
        />
        <ArcoButton
          type="primary"
          onClick={handle}
          disabled={isPage || !name.trim()}
        >
          Save
        </ArcoButton>
      </div>
    </div>
  );
}

function SyncedBlocksList({ blocks, onDelete }) {
  if (!blocks || blocks.length === 0)
    return (
      <p style={{ fontSize: 12, color: "#94a3b8", padding: "8px 12px", margin: 0, width: "100%" }}>
        No synced blocks yet.
      </p>
    );

  return (
    <div style={{ padding: "0 12px", width: "100%", marginTop: 4 }}>
      {blocks.map((b, index) => {
        const blockData = b.data || {};
        const blockType = blockData.type || b.type || BasicType.TEXT;
        const payloadData =
          blockData.data !== undefined ? blockData.data : b.data || {};
        const payloadChildren = blockData.children || b.children || [];
        const payloadAttributes =
          blockData.attributes || b.attributes || {};
        const itemKey = b.id ? `sb_${b.id}` : `sb_item_${b.name || index}_${index}`;

        return (
          <div
            key={itemKey}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 6,
            }}
          >
            <BlockAvatarWrapper
              type={blockType}
              payload={{
                type: blockType,
                data: payloadData,
                children: payloadChildren,
                attributes: payloadAttributes,
              }}
            >
              <div style={chipStyle} title={`Drag to add ${b.name}`}>
                ⣿ {b.name}
              </div>
            </BlockAvatarWrapper>
            <ArcoButton
              type="text"
              status="danger"
              size="mini"
              onClick={() => onDelete(b.id)}
            >
              ✕
            </ArcoButton>
          </div>
        );
      })}
    </div>
  );
}

function VariablesPanel({ mergeTags, setMergeTags, toast }) {
  const [newName, setNewName] = useState('');
  const [filter, setFilter] = useState('');

  function addVar() {
    const name = newName.trim();
    if (!name || name in mergeTags) return;
    setMergeTags((prev) => ({ ...prev, [name]: '' }));
    setNewName('');
  }

  function copyToClipboard(varText) {
    navigator.clipboard.writeText(varText);
    if (toast?.current) {
      toast.current.show({
        severity: 'info',
        summary: 'Copied',
        detail: `Copied ${varText} to clipboard`,
        life: 2000,
      });
    }
  }

  const keys = Object.keys(mergeTags).sort();
  const visible = filter
    ? keys.filter((k) => k.toLowerCase().includes(filter.toLowerCase()))
    : keys;

  return (
    <div style={{ padding: '0 12px 8px', width: '100%' }}>
      <ArcoInput
        placeholder="Filter variables…"
        value={filter}
        onChange={setFilter}
        allowClear
        style={{ marginBottom: 8 }}
      />
      {visible.length === 0 && (
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 8px' }}>
          No match.
        </p>
      )}
      {visible.map((key) => {
        const varTag = `{{${key}}}`;
        const varBlock = {
          type: "advanced#section",
          data: { value: {} },
          attributes: { padding: "10px 0px" },
          children: [
            {
              type: "advanced#column",
              data: { value: {} },
              attributes: { width: "100%" },
              children: [
                {
                  type: "advanced#text",
                  data: {
                    value: {
                      content: varTag,
                    },
                  },
                  attributes: {
                    "font-size": "14px",
                    color: "#1e293b",
                    padding: "10px 25px",
                  },
                  children: [],
                },
              ],
            },
          ],
        };

        return (
          <div key={`var_item_${key}`} style={{ marginBottom: 8 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 3,
              }}
            >
              <BlockAvatarWrapper
                type="advanced#section"
                payload={varBlock}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontFamily: 'monospace',
                    color: '#2563eb',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 4,
                    padding: '2px 6px',
                    cursor: 'grab',
                    userSelect: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    wordBreak: 'break-all',
                  }}
                  onClick={() => copyToClipboard(varTag)}
                  title="Drag variable to canvas or click to copy"
                >
                  ⣿ {varTag}
                </span>
              </BlockAvatarWrapper>
              <ArcoButton
                type="text"
                status="danger"
                size="mini"
                onClick={() =>
                  setMergeTags((prev) => {
                    const n = { ...prev };
                    delete n[key];
                    return n;
                  })
                }
              >
                ✕
              </ArcoButton>
            </div>
            <ArcoInput
              size="small"
              placeholder="sample test value"
              value={mergeTags[key] || ''}
              onChange={(value) =>
                setMergeTags((prev) => ({ ...prev, [key]: value }))
              }
            />
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <ArcoInput
          value={newName}
          placeholder="VariableName"
          onChange={(value) => setNewName(value)}
          onKeyDown={(e) => e.key === 'Enter' && addVar()}
        />
        <ArcoButton type="primary" onClick={addVar}>
          Add
        </ArcoButton>
      </div>
    </div>
  );
}

const renderPreviewHtmlWithMergeTags = (bodyHtml, designJson, mergeTags) => {
    let html = bodyHtml || '';

    if (designJson) {
        try {
            const context = { ...mergeTags };
            const mjmlString = JsonToMjml({
                data: designJson,
                mode: 'production',
                context: context
            });

            const converted = mjml(mjmlString, { validationLevel: 'skip' }).html;
            if (converted) html = converted;
        } catch (e) {
            // MJML compilation fallback
        }
    }

    if (!html) return '<p>No body content available</p>';

    if (mergeTags && typeof mergeTags === 'object') {
        Object.entries(mergeTags).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== '') {
                const patternWithDot = new RegExp(`\\{\\{\\s*\\.${key}\\s*\\}\\}`, 'g');
                const patternWithoutDot = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
                html = html.replace(patternWithDot, val).replace(patternWithoutDot, val);
            }
        });
    }

    return html;
};

const exportHtmlFromDesign = (pageData) => {
  if (!pageData) return "";

  const mjmlString = JsonToMjml({
    data: pageData,
    mode: "production",
    context: pageData,
  });

  return mjml(mjmlString, {
    validationLevel: "skip",
  }).html;
};

function PageDataSync({ onChange }) {
  const { pageData } = useEditorContext();
  const lastRef = useRef(null);

  useEffect(() => {
    if (pageData && lastRef.current !== pageData) {
      lastRef.current = pageData;
      onChange(pageData);
    }
  }, [pageData, onChange]);

  return null;
}

const EmailTemplates = () => {
  const dispatch = useDispatch();
  const adminData = useSelector((state) => state.adminAuth.adminSessionData);
  const headers = useMemo(() => {
    return { sessionid: adminData?.sessionId };
  }, [adminData?.sessionId]);

  const toast = useRef(null);
  const latestPageDataRef = useRef(null);

  const [rawTemplates, setRawTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchKeywords, setSearchKeywords] = useState("");

  // Dialog States
  const [templateDialog, setTemplateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [translateDialog, setTranslateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedDeleteLocale, setSelectedDeleteLocale] = useState("en");
  const [selectedTranslateLocales, setSelectedTranslateLocales] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewLocale, setPreviewLocale] = useState("en");
  const [filterOpen, setFilterOpen] = useState(false);

  const [editorData, setEditorData] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [editorTab, setEditorTab] = useState('editor');

  // Editor config
  const [mergeTags, setMergeTags] = useState({});
  const [savedBlocks, setSavedBlocks] = useState([]);

  const editorConfigLoaded = useRef(false);
  const skipAutoSaveRef = useRef(false);
  const saveEditorConfigTimer = useRef(null);

  const toggleFilter = () => setFilterOpen(!filterOpen);

  // Fetch All Email Templates: GET /v1/email-templates
  const fetchTemplates = useCallback(() => {
    setLoading(true);
    getEmailTemplates(headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.templates || response.data?.data || [];
        setRawTemplates(list);
      } else {
        setRawTemplates([]);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response.error?.errorMsg || "Failed to fetch email templates",
        });
      }
      setLoading(false);
    });
  }, [dispatch, headers]);

  useEffect(() => {
    if (adminData?.sessionId) {
      fetchTemplates();
    }
  }, [adminData?.sessionId, fetchTemplates]);

  useEffect(() => {
    if (!adminData?.sessionId) return;

    getEditorConfig(headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const cfg = response.data || {};

        if (cfg.merge_tags) {
          setMergeTags(cfg.merge_tags);
        }

        if (Array.isArray(cfg.saved_blocks)) {
          setSavedBlocks(
            [...cfg.saved_blocks].sort((a, b) => a.name.localeCompare(b.name)),
          );
        }

        if (cfg.email_colors) {
          const { highlight, iosButton, dark, light } = cfg.email_colors;
          const brandColors = [highlight, iosButton, dark, light].filter(
            Boolean,
          );
          const existing = JSON.parse(
            localStorage.getItem("CURRENT_COLORS_KEY") || "[]",
          );
          const merged = [...new Set([...brandColors, ...existing])].slice(
            0,
            20,
          );
          localStorage.setItem("CURRENT_COLORS_KEY", JSON.stringify(merged));
        }

        skipAutoSaveRef.current = true;
      }

      editorConfigLoaded.current = true;
    });
  }, [adminData?.sessionId, headers, dispatch]);

  useEffect(() => {
    window.__eePreviewTags = mergeTags;
  }, [mergeTags]);

  useEffect(() => {
    if (!adminData?.sessionId || !editorConfigLoaded.current) return;

    if (skipAutoSaveRef.current) {
      skipAutoSaveRef.current = false;
      return;
    }

    clearTimeout(saveEditorConfigTimer.current);

    saveEditorConfigTimer.current = setTimeout(() => {
      updateEditorConfig(
        {
          merge_tags: mergeTags,
          saved_blocks: savedBlocks,
        },
        headers,
        dispatch,
        () => {},
      );
    }, 800);

    return () => clearTimeout(saveEditorConfigTimer.current);
  }, [mergeTags, savedBlocks, adminData?.sessionId, headers, dispatch]);

  // Lock background scroll when any dialog is open
  useEffect(() => {
    const isAnyDialogOpen = templateDialog || previewDialog || translateDialog || deleteDialog;
    if (isAnyDialogOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [templateDialog, previewDialog, translateDialog, deleteDialog]);

  const saveBlock = useCallback((name, data) => {
    setSavedBlocks((prev) =>
      [...prev, { id: `sb_${Date.now()}`, name, data }].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    );
  }, []);

  const deleteBlock = useCallback((id) => {
    setSavedBlocks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const handleSetMergeTags = useCallback(
    (updater) => setMergeTags(updater),
    [],
  );

  // Grouping templates dynamically by Type into single rows
  const groupedTemplates = useMemo(() => {
    const map = new Map();
    for (const item of rawTemplates) {
      const type = item.type || item.name;
      if (!type) continue;
      if (!map.has(type)) {
        map.set(type, {
          type: type,
          subject: item.subject || "",
          locales: [],
          items: [],
        });
      }
      const group = map.get(type);
      const loc = item.locale || "en";
      if (!group.locales.includes(loc)) {
        group.locales.push(loc);
      }
      group.items.push(item);
      if (loc === "en" || !group.subject) {
        group.subject = item.subject || "";
      }
    }

    map.forEach((group) => {
      group.locales.sort((a) => (a === "en" ? -1 : 1));
    });

    let result = Array.from(map.values()).sort((a, b) =>
      a.type.localeCompare(b.type),
    );

    if (searchKeywords) {
      const query = searchKeywords.toLowerCase();
      result = result.filter(
        (t) =>
          t.type.toLowerCase().includes(query) ||
          t.subject.toLowerCase().includes(query) ||
          t.locales.some((l) => l.toLowerCase().includes(query)),
      );
    }
    return result;
  }, [rawTemplates, searchKeywords]);

  // Available locales restricted to en and es
  const localesList = useMemo(() => {
    const set = new Set(["en", "es"]);
    return Array.from(set).map((loc) => ({
      label: `${loc.toUpperCase()} (${loc})`,
      value: loc,
    }));
  }, []);

  const formik = useFormik({
    initialValues: {
      type: "",
      locale: "en",
      subject: "",
      body: "",
      design_json: null,
    },
    validationSchema: Yup.object({
      type: Yup.string()
        .required("Template Type is required")
        .test("unique-type", "Template Type already exists", function (value) {
          if (!value || selectedGroup) return true;
          const trimmed = value.trim().toLowerCase();
          const exists = rawTemplates?.some(
            (t) => (t.type || t.name || "").toLowerCase() === trimmed
          );
          return !exists;
        }),
      locale: Yup.string().required("Locale is required"),
      subject: Yup.string().required("Subject is required"),
    }),
    onSubmit: (values) => {
      setActionLoading(true);

      const latestPageData = latestPageDataRef.current || values.design_json;

      const payload = {
        subject: values.subject,
        html: exportHtmlFromDesign(latestPageData),
        design_json: latestPageData,
      };

      updateEmailTemplate(
        values.type,
        values.locale,
        payload,
        headers,
        dispatch,
        (response) => {
          setActionLoading(false);

          if (response.result === "SUCCESS") {
            toast.current?.show({
              severity: "success",
              summary: "Success",
              detail: "Template saved successfully",
              life: 3000,
            });

            setTemplateDialog(false);
            fetchTemplates();
          } else {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: response.error?.errorMsg || "Failed to save template",
              life: 3000,
            });
          }
        },
      );
    },
  });

  const scrollToFirstError = useCallback((errors) => {
    const errorKeys = Object.keys(errors || {});
    if (errorKeys.length === 0) return;

    const firstErrorKey = errorKeys[0];
    const element =
      document.getElementById(firstErrorKey) ||
      document.querySelector(".p-invalid") ||
      document.querySelector(".p-error");

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof element.focus === "function") {
        element.focus();
      }
    }
  }, []);

  const handleSubmitWithScroll = async (e) => {
    if (e) e.preventDefault();
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      setTimeout(() => {
        scrollToFirstError(errors);
      }, 50);
    } else {
      formik.handleSubmit();
    }
  };

  const handleEditorChange = useCallback(
    (pageData) => {
      latestPageDataRef.current = pageData;

      // Avoid unnecessary Formik updates
      if (formik.values.design_json !== pageData) {
        formik.setFieldValue("design_json", pageData, false);
      }

      const html = exportHtmlFromDesign(pageData);

      if (formik.values.body !== html) {
        formik.setFieldValue("body", html, false);
      }
    },
    [formik],
  );

  const editorInitialValues = useMemo(
    () => ({
      subject: formik.values.subject || "",
      subTitle: "",
      content: editorData,
    }),
    [editorData, formik.values.subject],
  );

  const sortedMergeTags = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(mergeTags)
          .sort()
          .map((k) => [k, mergeTags[k]]),
      ),
    [mergeTags],
  );

  const openNew = () => {
    setSelectedGroup(null);

    const emptyPage = BlockManager.getBlockByType(BasicType.PAGE).create({});
    emptyPage.attributes["background-color"] = "#ffffff";
    emptyPage.children = [];

    setEditorData(emptyPage);
    setEditorKey((k) => k + 1);

    formik.resetForm({
      values: {
        type: "",
        locale: "en",
        subject: "",
        body: "",
        design_json: emptyPage,
      },
    });

    setTemplateDialog(true);
  };
  const editTemplateForLocale = (group, loc) => {
    setSelectedGroup(group);
    const existingItem =
      group.items.find((i) => i.locale === loc) || group.items[0] || {};
    setActionLoading(true);

    // GET /v1/email-templates/{type}/{locale}
    getEmailTemplateDetails(group.type, loc, headers, dispatch, (response) => {
      setActionLoading(false);
      const details =
        response.result === "SUCCESS" ? response.data : existingItem;
      const dObj = details.design_json || existingItem.design_json || null;
      const bContent =
        details.body ||
        details.html ||
        existingItem.body ||
        (dObj ? exportHtmlFromDesign(dObj) : "");

      const pageData =
        dObj ||
        (() => {
          const page = BlockManager.getBlockByType(BasicType.PAGE).create({});
          page.attributes["background-color"] = "#ffffff";
          page.children = [];
          return page;
        })();

      setEditorData(pageData);
      setEditorKey((k) => k + 1);

      formik.resetForm({
        values: {
          type: group.type,
          locale: loc,
          subject: details.subject || existingItem.subject || "",
          body: bContent,
          design_json: pageData,
        },
      });
      setTemplateDialog(true);
    });
  };

  const openPreview = (group, targetLoc = null) => {
    const loc = targetLoc || group.locales[0] || "en";
    setSelectedGroup(group);
    setPreviewLocale(loc);
    const existingItem =
      group.items.find((i) => i.locale === loc) || group.items[0] || {};
    setPreviewTemplate({
      type: group.type,
      locale: loc,
      subject: existingItem.subject || group.subject || "",
      body: existingItem.body || existingItem.html || "",
      design_json: existingItem.design_json || null,
    });
    setPreviewDialog(true);
  };

  const switchPreviewLocale = (loc) => {
    setPreviewLocale(loc);
    if (selectedGroup) {
      const existingItem =
        selectedGroup.items.find((i) => i.locale === loc) || {};
      setPreviewTemplate({
        type: selectedGroup.type,
        locale: loc,
        subject: existingItem.subject || selectedGroup.subject || "",
        body: existingItem.body || existingItem.html || "",
        design_json: existingItem.design_json || null,
      });
    }
  };

  const openLiveFormPreview = () => {
    setPreviewTemplate({
      type: formik.values.type || "Preview",
      locale: formik.values.locale || "en",
      subject: formik.values.subject || "Subject Preview",
      body: formik.values.body || "",
      design_json: formik.values.design_json,
    });
    setPreviewLocale(formik.values.locale || "en");
    setPreviewDialog(true);
  };

  const previewSubjectWithTags = useMemo(() => {
    if (!previewTemplate?.subject) return "";
    let subj = previewTemplate.subject;
    if (mergeTags && typeof mergeTags === "object") {
      Object.entries(mergeTags).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== "") {
          const patternWithDot = new RegExp(`\\{\\{\\s*\\.${key}\\s*\\}\\}`, "g");
          const patternWithoutDot = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g");
          subj = subj.replace(patternWithDot, val).replace(patternWithoutDot, val);
        }
      });
    }
    return subj;
  }, [previewTemplate?.subject, mergeTags]);

  const renderedPreviewBody = useMemo(() => {
    if (!previewTemplate) return "<p>No body content available</p>";
    return renderPreviewHtmlWithMergeTags(
      previewTemplate.body,
      previewTemplate.design_json,
      mergeTags,
    );
  }, [previewTemplate, mergeTags]);

  const confirmDeleteTemplate = (group) => {
    setSelectedGroup(group);
    setSelectedDeleteLocale(group.locales[0] || "en");
    setDeleteDialog(true);
  };

  // DELETE /v1/email-templates/{type}/{locale}
  const deleteTemplateAction = () => {
    if (!selectedGroup) return;
    setActionLoading(true);
    deleteEmailTemplate(
      selectedGroup.type,
      selectedDeleteLocale,
      headers,
      dispatch,
      (response) => {
        setActionLoading(false);
        setRawTemplates((prev) =>
          prev.filter(
            (t) =>
              !(
                t.type === selectedGroup.type &&
                t.locale === selectedDeleteLocale
              ),
          ),
        );
        toast.current?.show({
          severity: "success",
          summary: "Successful",
          detail: `Deleted ${selectedGroup.type} (${selectedDeleteLocale})`,
          life: 3000,
        });
        setDeleteDialog(false);
        fetchTemplates();
      },
    );
  };

  const openTranslateDialog = (group) => {
    setSelectedGroup(group);
    setSelectedTranslateLocales([]);
    setTranslateDialog(true);
  };

  // POST /v1/email-templates/{type}/translate
  const submitTranslate = () => {
    if (!selectedGroup || selectedTranslateLocales.length === 0) return;
    setActionLoading(true);
    translateEmailTemplate(
      selectedGroup.type,
      selectedTranslateLocales,
      headers,
      dispatch,
      (response) => {
        setActionLoading(false);
        toast.current?.show({
          severity: "success",
          summary: "Successful",
          detail: `Translation submitted for ${selectedGroup.type}`,
          life: 3000,
        });
        setTranslateDialog(false);
        fetchTemplates();
      },
    );
  };

  // POST /v1/email-templates/test-send
  const handleTestSend = (type, locale) => {
    if (!type || !locale) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Template type and locale are required for test send",
      });
      return;
    }
    setActionLoading(true);
    testSendEmailTemplate(
      { type, locale },
      headers,
      dispatch,
      (response) => {
        setActionLoading(false);
        if (response.result === "SUCCESS") {
          toast.current?.show({
            severity: "success",
            summary: "Successful",
            detail: `Test email sent successfully for ${type} (${locale.toUpperCase()})`,
            life: 3000,
          });
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: response.error?.errorMsg || "Failed to send test email",
          });
        }
      }
    );
  };

  // Render single row actions: Edit, Translate, Delete
  const actionBodyTemplate = (rowData) => {
    const canManage = hasPermission(
      adminData?.permission,
      PERMISSIONS.MANAGE_EMAIL_TEMPLATES
    );
    return (
      canManage && (<div className="flex gap-2 align-items-center">
          <PR.Button
            icon="pi pi-pencil"
            rounded
            outlined
            className="icon-button"
            onClick={() =>
              editTemplateForLocale(rowData, rowData.locales[0] || "en")
            }
            tooltip="Edit Template"
            tooltipOptions={{ position: "top" }}
          />
          <PR.Button
            icon="pi pi-globe"
            rounded
            outlined
            className="icon-button"
            onClick={() => openTranslateDialog(rowData)}
            tooltip="Translate Template"
            tooltipOptions={{ position: "top" }}
          />
          <PR.Button
            icon="pi pi-trash"
            rounded
            outlined
            severity="danger"
            className="icon-button delete"
            onClick={() => confirmDeleteTemplate(rowData)}
            tooltip="Delete Template"
            tooltipOptions={{ position: "top" }}
          />
      </div>
      )
    );
  };

  // Render Languages/Locales chips in a single row (Clicking language opens respective preview)
  const languagesBodyTemplate = (rowData) => {
    return (
      <div className="flex flex-wrap gap-2 align-items-center">
        {rowData.locales.map((loc) => (
          <span
            key={loc}
            className="language-badge active"
            onClick={() => openPreview(rowData, loc)}
            title={`Click to preview ${loc.toUpperCase()} template`}
            style={{ cursor: "pointer" }}
          >
            {loc.toUpperCase()}
          </span>
        ))}
      </div>
    );
  };

  const itemTemplate = (rowData) => {
    return (
      <div className="col-12 custom-bg" key={rowData.type}>
        <div className="custom-table-body">
          <div className="table-grid">
            <ul className="col-ul mobile-col-ul">
              <li className="userName">
                <div className="column-label">Template Type</div>
                <div className="column-data font-semibold">{rowData.type}</div>
              </li>
              <li className="userEmail">
                <div className="column-label">Subject</div>
                <div className="column-data">{rowData.subject}</div>
              </li>
              <li className="role">
                <div className="column-label">Languages</div>
                <div className="column-data">
                  {languagesBodyTemplate(rowData)}
                </div>
              </li>
              <li className="actions-tab">
                <div className="column-label">Actions</div>
                <div className="column-data">{actionBodyTemplate(rowData)}</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="main email-templates-container">
      <div className="layout-sidebar">
        <AdminHeader />
      </div>
      <div className="layout-content-wrapper relative">
        <section className="admin-users-container">
          <div className="grid grid-nogutter">
            <div className="col-12">
              <div className="heading-sec mb-4 mobile-header-flex">
                <div className="flex align-items-center justify-content-between mobile-flex">
                  <h1>Email Templates</h1>
                  <div className="flex align-items-center justify-content-between mobile-screen-width gap-3">
                    {hasPermission(
                      adminData?.permission,
                      PERMISSIONS.MANAGE_EMAIL_TEMPLATES
                    ) && (
                      <div className="right-section flex align-items-center gap-3">
                        <PR.Button
                          label="Test Send"
                          icon="pi pi-send"
                          className="confirm-button reset-btn"
                          onClick={() => handleTestSend("Welcome", "en")}
                          loading={actionLoading}
                        />
                        <PR.Button
                          label="Create Template"
                          icon="pi pi-plus"
                          className="create-button"
                          onClick={openNew}
                        />
                      </div>
                    )}
                    <PR.Button
                      text
                      className="filter-toggle-button"
                      onClick={toggleFilter}
                    >
                      <i
                        className={`pi ${filterOpen ? "pi-filter-slash" : "pi-filter-fill"}`}
                      ></i>
                    </PR.Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`filter-right mb-4 ${filterOpen ? "mobile-filter-section" : ""}`}
          >
            <div className="flex align-items-center mobile-flex gap-2">
              <div className="users-search-input">
                <span>
                  <PR.InputText
                    placeholder="Search by Type, Subject, or Language"
                    value={searchKeywords}
                    onChange={(e) => setSearchKeywords(e.target.value)}
                  />
                </span>
              </div>
              {searchKeywords && (
                <PR.Button
                  label="Reset"
                  className="resetBtn"
                  onClick={() => setSearchKeywords("")}
                />
              )}
            </div>
          </div>

          {/* Single Row per Template Type Grid */}
          <div className="users-data-table card">
            <div className="card datatable-scroll">
              <div className="custom-table">
                <div className="custom-table-header">
                  <div className="table-grid">
                    <ul className="col-ul">
                      <li className="userName">
                        <span>Template Type</span>
                      </li>
                      <li className="userEmail">
                        <span>Subject</span>
                      </li>
                      <li className="role">
                        <span>Languages</span>
                      </li>
                      <li className="actions-tab">
                        <span>Actions</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <PR.DataView
                  value={groupedTemplates}
                  itemTemplate={itemTemplate}
                  loading={loading}
                  emptyMessage="No email templates found."
                />
              </div>
            </div>
          </div>

          {/* Create / Edit Dialog */}
          <PR.Dialog
            visible={templateDialog}
            blockScroll
            className="create-template-dialog email-template-dialog"
            header={
              selectedGroup
                ? `Edit Email Template (${formik.values.type} - ${formik.values.locale.toUpperCase()})`
                : "Create Email Template"
            }
            draggable={false}
            resizable={false}
            modal
            footer={
              <div className="flex justify-content-between align-items-center gap-3 w-full buttons-sections">
                <PR.Button
                  label="Live Preview"
                  icon="pi pi-eye"
                  type="button"
                  className="confirm-button reset-btn"
                  onClick={openLiveFormPreview}
                />
                <div className="flex align-items-center gap-3">
                  <PR.Button
                    label="Cancel"
                    type="button"
                    className="confirm-button reset-btn min-width"
                    onClick={() => setTemplateDialog(false)}
                  />
                  <PR.Button
                    label="Save Template"
                    type="button"
                    onClick={handleSubmitWithScroll}
                    className="confirm-button min-width"
                    loading={actionLoading}
                  />
                </div>
              </div>
            }
            onHide={() => setTemplateDialog(false)}
          >
            <form onSubmit={handleSubmitWithScroll} autoComplete="off">
              {/* Single row for 3 top input fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '1rem', marginTop: '0.75rem' }}>
                <div className="field">
                  <label htmlFor="type">Template Type</label>
                  <PR.InputText
                    id="type"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!!selectedGroup}
                    className={
                      formik.errors.type && formik.touched.type
                        ? "p-invalid w-full"
                        : "w-full"
                    }
                    placeholder="Template Type"
                  />
                  {formik.errors.type && formik.touched.type && (
                    <small className="p-error">{formik.errors.type}</small>
                  )}
                </div>

                <div className="field">
                  <label>Language / Locale</label>
                  <PR.Dropdown
                    aria-label="Language / Locale"
                    name="locale"
                    value={formik.values.locale}
                    options={localesList}
                    onChange={(e) => {
                      const newLoc = e.value;
                      if (!newLoc) return;
                      formik.setFieldValue("locale", newLoc);

                      if (selectedGroup) {
                        editTemplateForLocale(selectedGroup, newLoc);
                      }
                    }}
                    placeholder="Select Language"
                    className="w-full"
                  />
                  {formik.errors.locale && formik.touched.locale && (
                    <small className="p-error">{formik.errors.locale}</small>
                  )}
                </div>

                <div className="field">
                  <label htmlFor="subject">Subject Line</label>
                  <PR.InputText
                    id="subject"
                    name="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={
                      formik.errors.subject && formik.touched.subject
                        ? "p-invalid w-full"
                        : "w-full"
                    }
                    placeholder="Subject"
                  />
                  {formik.errors.subject && formik.touched.subject && (
                    <small className="p-error">{formik.errors.subject}</small>
                  )}
                </div>
              </div>
              <div className="field mt-3">
                <label>Content Body & Layout</label>
                <div className="easy-email-wrapper">
                  {editorData && (
                    <div className="easy-email-editor-host">
                      <ConfigProvider locale={enUS}>
                        <EmailEditorProvider
                          key={editorKey}
                          data={editorInitialValues}
                          height="100%"
                          dashed={false}
                          onUploadImage={(blob) =>
                            Promise.resolve(URL.createObjectURL(blob))
                          }
                          onSubmit={() => {}}
                          mergeTags={sortedMergeTags}
                          onChangeMergeTag={(namePath, value) =>
                            setMergeTags((prev) => ({
                              ...prev,
                              [namePath]: value,
                            }))
                          }
                          enabledMergeTagsBadge
                          mergeTagGenerate={(key) =>
                            key.startsWith(".") ? `{{${key}}}` : `{{.${key}}}`
                          }
                          enabledLogic
                        >
                          {() => (
                            <>
                              <PageDataSync onChange={handleEditorChange} />

                              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {/* Editor Mode Header Tabs */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '8px 12px',
                                  background: '#f8fafc',
                                  borderBottom: '1px solid #e2e8f0',
                                  flexShrink: 0
                                }}>
                                  <button
                                    type="button"
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: 6,
                                      fontSize: 13,
                                      fontWeight: 600,
                                      border: '1px solid',
                                      borderColor: editorTab === 'editor' ? '#4f46e5' : '#cbd5e1',
                                      background: editorTab === 'editor' ? '#4f46e5' : '#ffffff',
                                      color: editorTab === 'editor' ? '#ffffff' : '#475569',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6
                                    }}
                                    onClick={() => setEditorTab('editor')}
                                  >
                                    🎨 Layout & Design
                                  </button>
                                  <button
                                    type="button"
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: 6,
                                      fontSize: 13,
                                      fontWeight: 600,
                                      border: '1px solid',
                                      borderColor: editorTab === 'blocks' ? '#4f46e5' : '#cbd5e1',
                                      background: editorTab === 'blocks' ? '#4f46e5' : '#ffffff',
                                      color: editorTab === 'blocks' ? '#ffffff' : '#475569',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6
                                    }}
                                    onClick={() => setEditorTab('blocks')}
                                  >
                                    ⧉ Predefined Blocks ({savedBlocks.length})
                                  </button>
                                  <button
                                    type="button"
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: 6,
                                      fontSize: 13,
                                      fontWeight: 600,
                                      border: '1px solid',
                                      borderColor: editorTab === 'variables' ? '#4f46e5' : '#cbd5e1',
                                      background: editorTab === 'variables' ? '#4f46e5' : '#ffffff',
                                      color: editorTab === 'variables' ? '#ffffff' : '#475569',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6
                                    }}
                                    onClick={() => setEditorTab('variables')}
                                  >
                                    {} Variables ({Object.keys(mergeTags).length})
                                  </button>
                                </div>

                                {/* Workspace with Persistent SimpleLayout and Toggleable Side Panels */}
                                <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                                  {/* Predefined Blocks Panel */}
                                  <div style={{
                                    width: 320,
                                    background: '#f8fafc',
                                    borderRight: '1px solid #e2e8f0',
                                    overflowY: 'auto',
                                    padding: '12px 4px 12px 0',
                                    flexShrink: 0,
                                    display: editorTab === 'blocks' ? 'block' : 'none'
                                  }}>
                                    <div style={{ padding: '0 12px 8px', borderBottom: '1px solid #e2e8f0', marginBottom: 8 }}>
                                      <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 2px', color: '#1e293b' }}>Predefined Blocks</h4>
                                      <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>Drag any block onto the canvas or save a selected canvas block.</p>
                                    </div>
                                    <SaveBlockButton onSave={saveBlock} />
                                    <SyncedBlocksList blocks={savedBlocks} onDelete={deleteBlock} />
                                  </div>

                                  {/* Variables Panel */}
                                  <div style={{
                                    width: 320,
                                    background: '#f8fafc',
                                    borderRight: '1px solid #e2e8f0',
                                    overflowY: 'auto',
                                    padding: '12px 4px 12px 0',
                                    flexShrink: 0,
                                    display: editorTab === 'variables' ? 'block' : 'none'
                                  }}>
                                    <div style={{ padding: '0 12px 8px', borderBottom: '1px solid #e2e8f0', marginBottom: 8 }}>
                                      <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 2px', color: '#1e293b' }}>Template Variables</h4>
                                      <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>Manage merge tags and live preview test values.</p>
                                    </div>
                                    <VariablesPanel mergeTags={mergeTags} setMergeTags={handleSetMergeTags} toast={toast} />
                                  </div>

                                  {/* Single Persistent Email Editor Canvas */}
                                  <div style={{ flex: 1, minWidth: 0, height: '100%' }}>
                                    <SimpleLayout>
                                      <EmailEditor />
                                    </SimpleLayout>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </EmailEditorProvider>
                      </ConfigProvider>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </PR.Dialog>

          {/* Email Live Preview Dialog with design_json rendering support */}
          <PR.Dialog
            visible={previewDialog}
            blockScroll
            header={`Email Preview: ${previewTemplate?.type || ""}`}
            className="create-template-dialog email-template-dialog"
            draggable={false}
            resizable={false}
            modal
            footer={
              <div className="flex justify-content-end w-full buttons-sections">
                <PR.Button
                  label="Close Preview"
                  icon="pi pi-times"
                  className="confirm-button min-width"
                  onClick={() => setPreviewDialog(false)}
                />
              </div>
            }
            onHide={() => setPreviewDialog(false)}
          >
            {previewTemplate && (
              <div className="preview-container">
                {selectedGroup && selectedGroup.locales.length > 1 && (
                  <div className="flex align-items-center gap-2 mb-3">
                    <span className="font-semibold text-sm">
                      Select Language:
                    </span>
                    {selectedGroup.locales.map((loc) => (
                      <span
                        key={loc}
                        className={`language-badge ${previewLocale === loc ? "active" : "inactive"}`}
                        onClick={() => switchPreviewLocale(loc)}
                      >
                        {loc.toUpperCase()}
                      </span>
                    ))}
                  </div>
                )}
                <div className="preview-header-meta">
                  <div className="meta-row">
                    <span className="meta-label">Subject: </span>
                    <span className="meta-value">
                      {previewSubjectWithTags}
                    </span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Language: </span>
                    <span className="meta-value">
                      {previewTemplate.locale.toUpperCase()}
                    </span>
                  </div>
                  {(() => {
                    const givenMergeTags = Object.entries(mergeTags || {}).filter(
                      ([_, v]) => v !== undefined && v !== null && String(v).trim() !== ''
                    );
                    if (givenMergeTags.length === 0) return null;
                    return (
                      <div className="meta-row mt-2 pt-2 border-top-1 surface-border flex align-items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-color-secondary">Sample Test Values:</span>
                        {givenMergeTags.map(([k, v]) => (
                          <span key={`prev_tag_${k}`} className="text-xs bg-primary-100 text-primary-700 px-2 py-1 border-round font-mono">
                            {`{{.${k}}}`} = {v}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div className="email-body-preview">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderedPreviewBody,
                    }}
                  />
                </div>
              </div>
            )}
          </PR.Dialog>

          {/* Translate Dialog */}
          <PR.Dialog
            visible={translateDialog}
            blockScroll
            header={`Translate Template (${selectedGroup?.type})`}
            className="create-template-dialog"
            draggable={false}
            resizable={false}
            modal
            footer={
              <div className="flex justify-content-end align-items-center gap-3 w-full buttons-sections">
                <PR.Button
                  label="Cancel"
                  type="button"
                  className="confirm-button reset-btn"
                  onClick={() => setTranslateDialog(false)}
                />
                <PR.Button
                  label="Translate"
                  type="button"
                  className="confirm-button min-width"
                  onClick={submitTranslate}
                  loading={actionLoading}
                  disabled={selectedTranslateLocales.length === 0}
                />
              </div>
            }
            onHide={() => setTranslateDialog(false)}
          >
            <div className="field mt-3">
              <label>Target Languages</label>
              <PR.MultiSelect
                value={selectedTranslateLocales}
                options={localesList}
                onChange={(e) => setSelectedTranslateLocales(e.value)}
                placeholder="Select Locales to Translate"
                className="w-full"
              />
            </div>
          </PR.Dialog>

          {/* Delete Confirmation Dialog */}
          <PR.Dialog
            visible={deleteDialog}
            blockScroll
            header="Confirm Delete"
            className="create-template-dialog"
            draggable={false}
            resizable={false}
            modal
            footer={
              <div className="flex justify-content-end align-items-center gap-3 w-full buttons-sections">
                <PR.Button
                  label="No"
                  icon="pi pi-times"
                  className="p-button-text confirm-button reset-btn"
                  onClick={() => setDeleteDialog(false)}
                />
                <PR.Button
                  label="Delete"
                  icon="pi pi-check"
                  severity="danger"
                  className="confirm-button yes"
                  onClick={deleteTemplateAction}
                  loading={actionLoading}
                />
              </div>
            }
            onHide={() => setDeleteDialog(false)}
          >
            <div className="confirmation-content flex-column align-items-start gap-3">
              <div className="flex align-items-center gap-2">
                <i className="pi pi-exclamation-triangle mr-2 text-danger" />
                <span>
                  Select language of <b>{selectedGroup?.type}</b> to delete:
                </span>
              </div>
              {selectedGroup && (
                <PR.Dropdown
                  value={selectedDeleteLocale}
                  options={selectedGroup.locales.map((loc) => ({
                    label: `Delete Language: ${loc.toUpperCase()}`,
                    value: loc,
                  }))}
                  onChange={(e) => setSelectedDeleteLocale(e.value)}
                  className="w-full mt-2"
                />
              )}
            </div>
          </PR.Dialog>
        </section>
        <AdminFooter />
      </div>
      <PR.Toast ref={toast} />
    </div>
  );
};

export default EmailTemplates;
